import { AxiosResponse } from "axios";
import {
  DispatchEntities,
  Dispatch,
  DispatchStt,
  UpdateDispatch,
  UpdateDispatchStt,
  DispatchTemporary
} from "@/domain/entities/Dispatch";
import { PaginationV2 } from "@/domain/entities/Pagination";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";

export class DispatchMapper {
  private _mapDispatchEntities(dispatch: any) {
    const sttList = dispatch.dispatch_stt_list ?? [];
    return new Dispatch({
      dispatchId: dispatch.dispatch_id,
      dispatchNo: dispatch.dispatch_no,
      dispatchStts: dispatch.dispatch_stts,
      dispatchSttPieces: dispatch.dispatch_stt_pieces,
      dispatchSttGrossWeights: dispatch.dispatch_stt_gross_weights,
      dispatchSttsFailed: dispatch.dispatch_stts_failed,
      dispatchSttPiecesFailed: dispatch.dispatch_stt_pieces_failed,
      dispatchSttGrossWeightsFailed: dispatch.dispatch_stt_gross_weights_failed,
      dispatchCreatedAt: dispatch.dispatch_created_at,
      dispatchCreatedByName: dispatch.dispatch_created_by_name,
      dispatchCreatedByRole: dispatch.dispatch_created_by_role,
      dispatchToDriverId: dispatch.dispatch_to_driver_id,
      dispatchToDriverName: dispatch.dispatch_to_driver_name,
      dispatchToDriverPhone: dispatch.dispatch_to_driver_phone,
      dispatchToHubId: dispatch.dispatch_to_hub_id,
      dispatchToHubName: dispatch.dispatch_to_hub_name,
      dispatchToCityCode: dispatch.dispatch_to_city_code,
      dispatchToShuttleId: dispatch.dispatch_to_shuttle_id,
      dispatchToShuttleName: dispatch.dispatch_to_shuttle_name,
      stts: sttList.map((stt: any) => {
        return new DispatchStt({
          ddId: stt.ds_id,
          ddSttNo: stt.ds_stt_no,
          ddPieces: stt.ds_pieces,
          ddGrossWeight: stt.ds_gross_weight
        });
      })
    });
  }

  convertDispatchListFromApi(res: AxiosResponse<any>) {
    const {
      data: { data, meta }
    } = res;
    return new DispatchEntities({
      pagination: new PaginationV2({
        allTotalData: meta.total_records,
        page: meta.page,
        limit: meta.limit,
        totalData: meta.total_records
      }),
      data: data.map((dispatch: any) => this._mapDispatchEntities(dispatch))
    });
  }

  convertDispatchDetailFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;
    return new Dispatch({
      dispatchId: data.dispatch_id,
      dispatchNo: data.dispatch_no,
      dispatchStts: data.dispatch_stts,
      dispatchSttPieces: data.dispatch_stt_pieces,
      dispatchSttGrossWeights: data.dispatch_stt_gross_weights,
      dispatchSttsFailed: data.dispatch_stts_failed,
      dispatchSttPiecesFailed: data.dispatch_stt_pieces_failed,
      dispatchSttGrossWeightsFailed: data.dispatch_stt_gross_weights_failed,
      dispatchCreatedAt: data.dispatch_created_at,
      dispatchCreatedByName: data.dispatch_created_by_name,
      dispatchCreatedByRole: data.dispatch_created_by_role,
      dispatchToCityCode: data.dispatch_to_city_code,
      dispatchToDriverId: data.dispatch_to_driver_id,
      dispatchToDriverName: data.dispatch_to_driver_name,
      dispatchToDriverPhone: data.dispatch_to_driver_phone,
      dispatchToHubId: data.dispatch_to_hub_id,
      dispatchToHubName: data.dispatch_to_hub_name,
      dispatchToShuttleId: data.dispatch_to_shuttle_id,
      dispatchToShuttleName: data.dispatch_to_shuttle_name,
      stts: (data.stts || []).map(
        (stt: any) =>
          new DispatchStt({
            ddId: stt.dd_id,
            ddSttNo: stt.dd_stt_no,
            ddProductType: stt.dd_product_type,
            ddDestinationCityId: stt.dt_destination_city_id,
            ddDestinationDistrictId: stt.dd_destination_district_id,
            ddDestinationDistrictName: stt.dd_destination_district_name,
            ddDestinationUrsaCode: stt.dd_destination_ursa_code,
            ddRefNo: stt.dd_ref_no,
            ddDemNo: stt.dd_dem_no,
            ddPieces: stt.dd_pieces,
            ddGrossWeight: stt.dd_gross_weight,
            ddRecipientName: stt.dd_recipient_name,
            ddRecipientPhone: stt.dd_recipient_phone,
            ddRecipientAddress: stt.dd_recipient_address,
            ddLastStatus: stt.dd_last_status,
            ddScanStatus: stt.dd_scan_status,
            ddIsCod: Boolean(stt.dd_is_cod),
            ddIsDfod: Boolean(stt.dd_is_dfod)
          })
      )
    });
  }

  convertUpdateDispatchFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;
    const stt = data.stt || {};
    return new UpdateDispatch({
      stt: new UpdateDispatchStt({
        sttId: stt.stt_id,
        sttNo: stt.stt_no,
        sttProductType: stt.stt_product_type,
        sttDestinationCityId: stt.stt_destination_city_id,
        sttDestinationCityName: stt.stt_destination_city_name,
        sttDestinationDistrictId: stt.stt_destination_district_id,
        sttDestinationDistrictName: stt.stt_destination_district_name,
        sttDestinationDistrictUrsaCode: stt.stt_destination_district_ursa_code,
        refNo: stt.ref_no,
        demNo: stt.dem_no,
        sttTotalPiece: stt.stt_total_piece,
        sttCommodityCode: stt.stt_commodity_code,
        sttCommodityName: stt.stt_commodity_name,
        sttGrossWeight: stt.stt_gross_weight,
        sttRecipientName: stt.stt_recipient_name,
        sttRecipientPhone: stt.stt_recipient_phone,
        sttRecipientAddress: stt.stt_recipient_address,
        sttLastStatusId: stt.stt_last_status_id,
        sttCodAmount: stt.stt_cod_amount,
        dtScanStatus: stt.dt_scan_status,
        dtCreatedAt: stt.dt_created_at,
        dtUpdatedAt: stt.dt_updated_at,
        dtScanNo: stt.dt_scan_no,
      }),
      dtDemTotal: data.dt_dem_total,
      dtDemTotalNow: data.dt_dem_total_now,
      demStatus: data.dem_status,
      dem: (data.dem || []).map(
        (dem: any) =>
          new UpdateDispatchStt({
            sttId: dem.stt_id,
            sttNo: dem.stt_no,
            sttProductType: dem.stt_product_type,
            sttDestinationCityId: dem.stt_destination_city_id,
            sttDestinationCityName: dem.stt_destination_city_name,
            sttDestinationDistrictId: dem.stt_destination_district_id,
            sttDestinationDistrictName: dem.stt_destination_district_name,
            sttDestinationDistrictUrsaCode:
              dem.stt_destination_district_ursa_code,
            demNo: dem.dem_no,
            sttTotalPiece: dem.stt_total_piece,
            sttCommodityCode: dem.stt_commodity_code,
            sttCommodityName: dem.stt_commodity_name,
            sttGrossWeight: dem.stt_gross_weight,
            sttRecipientName: dem.stt_recipient_name,
            sttRecipientPhone: dem.stt_recipient_phone,
            sttRecipientAddress: dem.stt_recipient_address,
            sttLastStatusId: dem.stt_last_status_id,
            sttCodAmount: dem.stt_cod_amount,
            dtScanStatus: dem.dt_scan_status,
            dtCreatedAt: dem.dt_created_at,
            dtUpdatedAt: dem.dt_updated_at,
            dtScanNo: dem.dt_scan_no
          })
      )
    });
  }

  convertListTemporaryFromApi(res: AxiosResponse<any>) {
    const {
      data: { data, meta }
    } = res;
    return new ResponsePayloadV2({
      pagination: new PaginationV2({
        page: meta?.page,
        limit: meta?.limit,
        totalData: meta?.total_records
      }),
      data: data?.length
        ? data?.map(
            (item: any) =>
              new DispatchTemporary({
                dtId: item.dt_id,
                dtAccountId: item.dt_account_id,
                dtIsActive: Boolean(item.dt_is_active),
                dtSttNo: item.dt_stt_no,
                dtProductType: item.dt_product_type,
                dtDestinationCityId: item.dt_destination_city_id,
                dtDestinationDistrictId: item.dt_destination_district_id,
                dtDestinationDistrictName: item.dt_destination_district_name,
                dtDestinationDistrictUrsaCode:
                  item.dt_destination_district_ursa_code,
                dtScanNo: item.dt_scan_no,
                dtRefNo: item.dt_ref_no,
                dtDemNo: item.dt_dem_no,
                dtPieces: item.dt_pieces,
                dtChargeableWeight: item.dt_gross_weight,
                dtRecipientName: item.dt_recipient_name,
                dtRecipientPhone: item.dt_recipient_phone,
                dtRecipientAddress: item.dt_recipient_address,
                dtScanStatus: item.dt_scan_status,
                dtDemStatus: item.dt_dem_status,
                sttLastStatusId: item.stt_last_status_id,
                dtMeta: item.dt_meta || {},
                dtCreatedAt: item.dt_created_at,
                dtUpdatedAt: item.dt_updated_at
              })
          )
        : []
    });
  }

  convertProcessUpdateDispatchFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;
    return new ResponsePayloadV2({
      data: new Dispatch({
        dispatchId: data?.dispatch_id,
        dispatchNo: data?.dispatch_code
      })
    });
  }
}
